import type { MouseEvent, ReactNode, Ref } from "react";
import { Link as RouterLink, useLocation } from "react-router";

import openExternal from "~/util/openExternal";

interface LinkProps {
  children: ReactNode;
  href?: string;
  activeClass?: string;
  className?: string;
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  ref?: Ref<HTMLAnchorElement>;
  target?: "_blank" | "_self" | "_parent" | "_top";
}

/**
 * Link component.
 *
 * This component is used to create a link that can be either internal or external.
 * It uses RouterLink for internal links, and <a> for external links.
 *
 * @param {ReactNode} children - The children to be rendered inside the link.
 * @param {string} href - The URL of the link.
 * @param {string} activeClass - The class to be added when the link is active.
 * @param {string} className - The class to be added to the link.
 * @param {(event: MouseEvent<HTMLAnchorElement>) => void} onClick - The function to be called when the link is clicked.
 * @param {any} rest - Any other props to be passed to the link.
 * @param {Ref<HTMLAnchorElement>} ref - The ref to be passed to the link.
 */
function Link({
  children,
  href = "",
  activeClass,
  className,
  onClick,
  ref,
  ...rest
}: LinkProps) {
  const pathname = useLocation().pathname;

  let classes = className;

  // This assumes that any internal link will start with
  // exactly one slash, and that anything else is external.
  const internal = /^[/#](?!\/)/.test(href);

  // Use NextLink for internal, and <a> for external.
  if (internal || onClick) {
    const cleanHref = href;

    if (!onClick) {
      if (href[0] !== "#") {
        // Only add trailing slash if there are no search params or hash
        // const hasSearchOrHash = href.includes("?") || href.includes("#");
        // if (!hasSearchOrHash) {
        //   cleanHref = href.replace(/\/?$/, "/");
        // }
      }

      if (pathname === cleanHref) {
        classes += ` ${activeClass}`;
      }
    }

    return (
      <RouterLink
        to={cleanHref}
        onClick={onClick}
        className={classes}
        ref={ref}
        {...rest}
      >
        {children}
      </RouterLink>
    );
  }
  return (
    <a
      href={href}
      className={classes}
      rel="noopener noreferrer"
      target="_blank"
      onClick={(e) => {
        e.preventDefault();
        openExternal(href);
      }}
      ref={ref}
      {...rest}
    >
      {children}
    </a>
  );
}

export default Link;
